import React, { useState, useEffect } from 'react';
import { firestore } from './firebase';
import db from './services/db';
import { doc as fsDoc, runTransaction as fsRunTransaction, increment } from 'firebase/firestore';
import { createProductUrl } from './utils/slug';
import './Products.css';

function normalizePlans(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  return Object.values(raw);
}

// ── Variants helpers ─────────────────────────────────────────────────────────
// Stored under product.variants (admin panel). Each variant: label, price,
// originalPrice/discountedPrice, installmentPlans[], colors[]. Each color has
// name, hex, images[].
function getVariants(product) {
  if (!product?.hasVariants) return [];
  const v = product.variants;
  if (!v) return [];
  return Array.isArray(v) ? v.filter(Boolean) : Object.values(v).filter(Boolean);
}
function variantColors(variant) {
  if (!variant) return [];
  const c = variant.colors;
  if (!c) return [];
  return Array.isArray(c) ? c.filter(Boolean) : Object.values(c).filter(Boolean);
}
function colorImages(color) {
  if (!color) return [];
  const i = color.images;
  if (!i) return [];
  return Array.isArray(i) ? i.filter(Boolean) : Object.values(i).filter(Boolean);
}

// Resolve the product's color list. New products store colors at top level
// (shared across all variants). Legacy products have colors per variant — we
// merge unique colors from every variant so the UI works for both shapes.
function getProductColors(product) {
  if (!product) return [];
  const direct = Array.isArray(product.colors) ? product.colors
               : product.colors ? Object.values(product.colors) : [];
  const filteredDirect = direct.filter(Boolean);
  if (filteredDirect.length > 0) return filteredDirect;
  const variants = getVariants(product);
  if (variants.length === 0) return [];
  const seen = new Map();
  for (const v of variants) {
    for (const c of variantColors(v)) {
      if (!c) continue;
      const key = (c.name || '').toLowerCase().trim();
      if (!key) continue;
      if (!seen.has(key)) seen.set(key, c);
    }
  }
  return Array.from(seen.values());
}
// Lowest enabled advance amount across all plans on a product, rounded UP
// to the nearest 100 (matches the Qist Calculator rounding so customers
// always see the advance bucketed to a whole hundred). For variant products,
// scans every plan on every variant. Returns null when no plan has an
// enabled advance, or when there are no plans at all.
export function lowestAdvance(product) {
  const allPlans = [];
  const variants = getVariants(product);
  if (variants.length > 0) {
    variants.forEach(v => {
      normalizePlans(v.installmentPlans).forEach(p => allPlans.push(p));
    });
  } else {
    normalizePlans(product?.installmentPlans).forEach(p => allPlans.push(p));
  }
  const advances = allPlans
    .filter(p => p && p.advance && p.advance.enabled && p.advance.amount)
    .map(p => Number(p.advance.amount))
    .filter(n => Number.isFinite(n) && n > 0);
  if (advances.length === 0) return null;
  return Math.ceil(Math.min(...advances) / 100) * 100;
}

// Cash price for a variant (discountedPrice if discount set, else price).
function variantCashPrice(v) {
  if (!v) return 0;
  if (v.discountedPrice && v.originalPrice && Number(v.discountedPrice) < Number(v.originalPrice)) {
    return Number(v.discountedPrice);
  }
  return Number(v.price) || 0;
}

// Resolve a product's primary image array. For variant products that have no
// top-level images uploaded (admin only uploads images per color now), fall
// back to the first variant's first color's first image.
function productPrimaryImages(product) {
  let imgs = [];
  if (product.images) {
    imgs = Array.isArray(product.images) ? product.images : Object.values(product.images);
  } else if (product.imageUrl) {
    imgs = [product.imageUrl];
  }
  imgs = imgs.filter(Boolean);
  if (imgs.length > 0) return imgs;
  // Falls back to the first product-level color's images (new shape) or the
  // first color of the first variant (legacy shape) — getProductColors handles
  // both transparently.
  const productColors = getProductColors(product);
  if (productColors.length === 0) return [];
  return colorImages(productColors[0]);
}

function trackView(productId) {
  db.updateDoc('analytics', 'views', { [productId]: increment(1) });
}

// 2-letter codes for the major cities. Any city not in this map falls back
// to the first 2 letters of the city name (uppercased, spaces stripped).
const CITY_CODES = {
  Quetta: 'QT',
  Karachi: 'KR',
  Lahore: 'LH',
  Islamabad: 'IS',
  Peshawar: 'PW',
  Multan: 'MT',
  Faisalabad: 'FS',
  Rawalpindi: 'RW',
  Hyderabad: 'HY',
  Sialkot: 'SK',
  Gujranwala: 'GW',
  Abbottabad: 'AB',
  Mardan: 'MD',
  Bahawalpur: 'BW',
  Sargodha: 'SR',
};

function cityCode(city) {
  if (!city) return 'XX';
  if (CITY_CODES[city]) return CITY_CODES[city];
  const clean = String(city).replace(/\s+/g, '').toUpperCase();
  return clean.length >= 2 ? clean.slice(0, 2) : 'XX';
}

// Generates a customer-facing serial in the format CC YY T NNN, e.g. QT26I001
//   CC  = 2-letter city code (from customer's selected city)
//   YY  = current year (last 2 digits)
//   T   = 'C' for cash, 'I' for installment
//   NNN = sequence number (zero-padded; atomic counter in Firebase)
//
// The sequence lives at /orderCounters/{cash|installment} and is bumped via
// a Firebase transaction (two simultaneous customers cannot get the same
// number). If the transaction fails for ANY reason — counter rule not yet
// deployed, network error, etc. — we fall back to a time-based suffix so the
// order still goes through and the customer never sees a permission error
// just because of serial bookkeeping.
async function generateSerial(type, city) {
  const cityCd = cityCode(city);
  const yy     = String(new Date().getFullYear()).slice(-2);
  const typeCd = type === 'cash' ? 'C' : 'I';
  let suffix;
  try {
    let num = 1;
    await fsRunTransaction(firestore, async (t) => {
      const counterRef = fsDoc(firestore, 'orderCounters', type);
      const snap = await t.get(counterRef);
      const current = snap.exists() ? (Number(snap.data()?.count) || 0) : 0;
      num = current + 1;
      t.set(counterRef, { count: num });
    });
    suffix = String(num).padStart(3, '0');
  } catch (_) {
    // Time-based fallback: last 3 digits of seconds-into-day. Never blocks
    // an order on a counter problem.
    const d = new Date();
    const secs = d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds();
    suffix = String(secs % 1000).padStart(3, '0');
  }
  return `${cityCd}${yy}${typeCd}${suffix}`;
}

// ── Pakistan locations ────────────────────────────────────────────────────────

const PAKISTAN_LOCATIONS = {
  Punjab:               ['Lahore', 'Faisalabad', 'Rawalpindi', 'Gujranwala', 'Multan', 'Bahawalpur', 'Sargodha', 'Sialkot', 'Sheikhupura', 'Rahim Yar Khan', 'Jhang', 'Dera Ghazi Khan', 'Gujrat', 'Sahiwal', 'Wazirabad', 'Okara', 'Kasur', 'Chiniot', 'Hafizabad', 'Mandi Bahauddin', 'Sadiqabad'],
  Sindh:                ['Karachi', 'Hyderabad', 'Sukkur', 'Larkana', 'Nawabshah', 'Mirpur Khas', 'Jacobabad', 'Shikarpur', 'Khairpur', 'Dadu'],
  KPK:                  ['Peshawar', 'Mardan', 'Mingora', 'Kohat', 'Abbottabad', 'Mansehra', 'Dera Ismail Khan', 'Swabi', 'Nowshera', 'Charsadda'],
  Balochistan:          ['Quetta', 'Turbat', 'Khuzdar', 'Hub', 'Chaman', 'Gwadar', 'Zhob', 'Loralai', 'Dera Murad Jamali'],
  AJK:                  ['Muzaffarabad', 'Mirpur', 'Rawalakot', 'Kotli', 'Bagh'],
  'Gilgit-Baltistan':   ['Gilgit', 'Skardu', 'Chilas', 'Hunza'],
  Islamabad:            ['Islamabad'],
};

// ── Order Modal ───────────────────────────────────────────────────────────────

export function OrderModal({ product, type, onClose }) {
  const variants = getVariants(product);
  const hasVariants = variants.length > 0;

  const [vIdx, setVIdx] = useState(0);
  const [cIdx, setCIdx] = useState(0);
  const [imgIdx, setImgIdx] = useState(0);

  const selectedVariant = hasVariants ? variants[vIdx] : null;
  // Colors are now product-level (shared across all variants). For legacy
  // products with colors stored under variants[].colors[], getProductColors
  // merges them so this works with both old and new data.
  const colors = hasVariants ? getProductColors(product) : [];
  const selectedColor = colors[cIdx] || null;
  const variantImages = (hasVariants && colors.length > 0) ? colorImages(selectedColor) : [];

  // Reset color + image when variant changes (different variants may have
  // different color sets).
  useEffect(() => { setCIdx(0); setImgIdx(0); }, [vIdx]);
  useEffect(() => { setImgIdx(0); }, [cIdx]);

  // Plans: variant-aware when variants exist, falls back to product-level otherwise.
  const plans = type === 'installment'
    ? (hasVariants
        ? normalizePlans(selectedVariant?.installmentPlans)
        : (product.installmentPlans
            ? normalizePlans(product.installmentPlans)
            : [
                ...(product.installment3  ? [{ months: 3,  amount: product.installment3  }] : []),
                ...(product.installment6  ? [{ months: 6,  amount: product.installment6  }] : []),
                ...(product.installment12 ? [{ months: 12, amount: product.installment12 }] : []),
              ]))
    : [];

  const emptyForm = type === 'cash'
    ? { name: '', phone: '', altPhone: '', province: '', city: '', address: '', quantity: '1' }
    : { name: '', phone: '', altPhone: '', cnic: '', province: '', city: '', address: '', jobTitle: '', monthlyIncome: '', planIndex: '0' };

  const [form, setForm]       = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [done, setDone]       = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [orderSerial, setOrderSerial] = useState('');

  // Two-stage modal: 'overview' = product + variant + plan picker (or cash
  // price); 'form' = the existing order form. Buy Now / Next promotes to
  // 'form'. Customer can navigate Back to revisit the overview.
  const [stage, setStage] = useState('overview');
  // Installment-mode requirement: customer must pick a plan before Next.
  // null = no plan selected yet; bind to form.planIndex once picked.
  const [pickedPlanIdx, setPickedPlanIdx] = useState(null);
  const [planShake, setPlanShake]         = useState(false);

  // Reset selected plan when variant changes (its plan list is different).
  useEffect(() => {
    setForm(f => ({ ...f, planIndex: '0' }));
    setPickedPlanIdx(null);
  }, [vIdx]);

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  // Main image: prefer the selected color's first image, then variant fallback,
  // then the product-level images.
  const productImages = productPrimaryImages(product);

  const galleryImages = (variantImages.length > 0 ? variantImages : productImages);
  const firstImage = galleryImages[imgIdx] || galleryImages[0] || '';

  // Effective cash price: variant > product-level discount > base.
  const effectivePrice = hasVariants
    ? variantCashPrice(selectedVariant)
    : (product.discount?.discountedPrice || product.price);
  const variantOriginal = hasVariants && selectedVariant?.originalPrice
                       && Number(selectedVariant.originalPrice) > variantCashPrice(selectedVariant)
    ? Number(selectedVariant.originalPrice) : null;

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitError('');
    try {
      const serialNumber = await generateSerial(type, form.city);
      const variantInfo = hasVariants ? {
        variantLabel: selectedVariant?.label || '',
        colorName:    selectedColor?.name || '',
        colorHex:     selectedColor?.hex || '',
      } : {};

      if (type === 'cash') {
        await db.pushDoc('cashOrders', {
          productId:    product.id,
          productName:  product.name,
          productImage: firstImage,
          name:         form.name,
          phone:        form.phone,
          altPhone:     form.altPhone || '',
          province:     form.province,
          city:         form.city,
          address:      form.address,
          quantity:     Number(form.quantity),
          price:        Number(effectivePrice),
          ...variantInfo,
          serialNumber,
          status:       'pending',
          createdAt:    Date.now(),
          company:      'al-sami',
        });
      } else {
        const plan = plans[Number(form.planIndex)] || plans[0] || null;
        await db.pushDoc('installmentOrders', {
          productId:     product.id,
          productName:   product.name,
          productImage:  firstImage,
          name:          form.name,
          phone:         form.phone,
          altPhone:      form.altPhone || '',
          cnic:          form.cnic,
          province:      form.province,
          city:          form.city,
          address:       form.address,
          jobTitle:      form.jobTitle,
          monthlyIncome: form.monthlyIncome,
          selectedPlan:  plan,
          ...variantInfo,
          serialNumber,
          status:        'pending',
          createdAt:     Date.now(),
          company:       'al-sami',
        });

      }
      setOrderSerial(serialNumber);
      setDone(true);
    } catch (err) {
      setSubmitError('Something went wrong. Please check your connection and try again.');
    }
    setLoading(false);
  };

  return (
    <div className="om-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="om-box">
        <div className="om-header">
          <div className="om-product-info">
            {firstImage && <img src={firstImage} alt={product.name} className="om-product-img" />}
            <div>
              <p className="om-product-name">{product.name}</p>
              <p className="om-product-type">{type === 'cash' ? 'Cash Purchase' : 'Installment Application'}</p>
            </div>
          </div>
          <button className="om-close" type="button" onClick={onClose}>✕</button>
        </div>

        {hasVariants && !done && (
          <div className="om-variant-block">
            {/* Image gallery — crossfades when color or thumbnail changes */}
            {galleryImages.length > 0 && (
              <div className="om-variant-gallery">
                <div className="om-variant-main-wrap">
                  <img
                    key={`${vIdx}-${cIdx}-${imgIdx}`}
                    src={firstImage}
                    alt={`${product.name} ${selectedColor?.name || ''}`}
                    className="om-variant-main"
                  />
                </div>
                {galleryImages.length > 1 && (
                  <div className="om-variant-thumbs">
                    {galleryImages.map((url, i) => (
                      <button
                        key={i}
                        type="button"
                        className={`om-variant-thumb${i === imgIdx ? ' active' : ''}`}
                        onClick={() => setImgIdx(i)}
                      >
                        <img src={url} alt="" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Storage chips */}
            <p className="om-variant-section-label">Storage</p>
            <div className="om-variant-chips">
              {variants.map((v, i) => (
                <button
                  key={i}
                  type="button"
                  className={`om-variant-chip${i === vIdx ? ' active' : ''}`}
                  onClick={() => setVIdx(i)}
                >
                  {v.label || `Variant ${i + 1}`}
                </button>
              ))}
            </div>

            {/* Color circles */}
            {colors.length > 0 && (
              <>
                <p className="om-variant-section-label">
                  Color:{selectedColor?.name && <span className="om-variant-color-name"> {selectedColor.name}</span>}
                </p>
                <div className="om-variant-colors">
                  {colors.map((c, i) => {
                    const thumbSrc = colorImages(c)[0];
                    return (
                      <button
                        key={i}
                        type="button"
                        title={c.name || `Color ${i + 1}`}
                        aria-label={c.name || `Color ${i + 1}`}
                        className={`om-variant-color${i === cIdx ? ' active' : ''}`}
                        onClick={() => setCIdx(i)}
                      >
                        {thumbSrc
                          ? <img src={thumbSrc} alt="" className="om-variant-color-thumb" />
                          : <span className="om-variant-color-thumb om-variant-color-thumb--empty" />
                        }
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {/* Price (cash) — fades on variant change */}
            {type === 'cash' && (
              <div key={vIdx} className="om-variant-price-row">
                {variantOriginal && (
                  <span className="om-variant-price-original">Rs. {variantOriginal.toLocaleString()}</span>
                )}
                <span className="om-variant-price">Rs. {Number(effectivePrice).toLocaleString()}</span>
              </div>
            )}

            {/* Installment summary line — fades on variant change.
                Shows ONLY the lowest enabled advance across this variant's
                plans, rounded up to the nearest 100. Matches the card design:
                no monthly payment, no /mo, just the advance figure. */}
            {type === 'installment' && (() => {
              const advances = plans
                .filter(p => p && p.advance && p.advance.enabled && p.advance.amount)
                .map(p => Number(p.advance.amount))
                .filter(n => Number.isFinite(n) && n > 0);
              if (advances.length === 0) return null;
              const adv = Math.ceil(Math.min(...advances) / 100) * 100;
              return (
                <p key={vIdx} className="om-variant-installment-summary">
                  Rs. {adv.toLocaleString()}{' '}
                  <span className="product-advance-word">Advance</span>
                </p>
              );
            })()}
          </div>
        )}

        {/* Stage A — overview: product is configured (variant/color picked
            for variant products), plan is selected (for installment), then
            customer clicks Buy Now (cash) or Next → (installment) to advance
            to the form. The form fields below are hidden until then. */}
        {!done && stage === 'overview' && (
          <div className="om-overview-actions">
            {type === 'installment' && plans.length > 0 && (
              <div className={`om-plans-overview${planShake ? ' om-plans-overview--shake' : ''}`}>
                <p className="om-overview-label">Select Installment Plan</p>
                <div className="om-plans">
                  {plans.map((plan, i) => {
                    const r100     = (n) => Math.ceil((Number(n) || 0) / 100) * 100;
                    const months   = Number(plan.months) || 0;
                    const monthly  = r100(plan.amount);
                    const advance  = plan.advance?.enabled ? r100(plan.advance.amount) : 0;
                    const monthlyMonths = Math.max(0, months - 1);
                    const total    = advance + monthly * monthlyMonths;
                    const isPopular = months === 12;
                    const isPicked = pickedPlanIdx === i;
                    return (
                      <button
                        key={i}
                        type="button"
                        className={`om-plan${isPicked ? ' selected' : ''}`}
                        style={{ animationDelay: `${i * 60}ms` }}
                        onClick={() => {
                          setPickedPlanIdx(i);
                          set('planIndex', String(i));
                        }}
                      >
                        <span className="om-plan-head">
                          <span className="om-plan-title">{months} Months Plan</span>
                          {isPopular && <span className="om-plan-badge">MOST POPULAR</span>}
                        </span>
                        <span className="om-plan-row">
                          <span className="om-plan-key">Advance / ایڈوانس</span>
                          <span className="om-plan-val">Rs. {advance.toLocaleString()}</span>
                        </span>
                        <span className="om-plan-row">
                          <span className="om-plan-key">Monthly / ماہانہ قسط</span>
                          <span className="om-plan-val">
                            <b>Rs. {monthly.toLocaleString()}</b>
                            <span className="om-plan-x"> × {monthlyMonths} months</span>
                          </span>
                        </span>
                        <span className="om-plan-row om-plan-row--total">
                          <span className="om-plan-key">Total / کل رقم</span>
                          <span className="om-plan-val om-plan-total-val">Rs. {total.toLocaleString()}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {type === 'cash' ? (
              <button
                type="button"
                className="om-cta-primary"
                onClick={() => setStage('form')}
              >
                Buy Now
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="om-cta-primary"
                  onClick={() => {
                    if (pickedPlanIdx === null) {
                      setPlanShake(true);
                      setSubmitError('Please select a plan first');
                      setTimeout(() => setPlanShake(false), 600);
                      return;
                    }
                    setSubmitError('');
                    setStage('form');
                  }}
                >
                  Next →
                </button>
                {submitError && <p className="om-error om-overview-error">{submitError}</p>}
              </>
            )}
          </div>
        )}

        {done ? (
          <div className="om-success">
            <div className="om-success-icon">✓</div>
            <h3>{type === 'cash' ? 'Order Placed!' : 'Application Submitted!'}</h3>
            <div className="om-serial-box">
              <span className="om-serial-label">Your Serial Number</span>
              <span className="om-serial-number">{orderSerial}</span>
              <span className="om-serial-hint">Save this to track your order status</span>
            </div>
            <p>
              {type === 'cash'
                ? 'Your order has been received. Our team will contact you shortly to confirm.'
                : 'Your application has been received. Our team will review and contact you.'}
            </p>
            <button className="om-done-btn" onClick={onClose}>Done</button>
          </div>
        ) : stage === 'form' ? (
          <form className="om-form" onSubmit={handleSubmit}>
            <button
              type="button"
              className="om-back-btn"
              onClick={() => { setStage('overview'); setSubmitError(''); }}
            >
              ← Back
            </button>
            <div className="om-field">
              <label>Full Name</label>
              <input type="text" value={form.name} onChange={e => set('name', e.target.value)}
                placeholder="Your full name" required />
            </div>

            <div className="om-row">
              <div className="om-field">
                <label>WhatsApp / Contact No</label>
                <input type="tel" value={form.phone}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 11);
                    set('phone', v);
                  }}
                  placeholder="03xx-xxxxxxx" required maxLength={11} />
              </div>
              <div className="om-field">
                <label>
                  Alternative Number
                  <span className="om-label-opt"> optional</span>
                </label>
                <input type="tel" value={form.altPhone}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 11);
                    set('altPhone', v);
                  }}
                  placeholder="03xx-xxxxxxx" maxLength={11} />
              </div>
            </div>

            {type === 'installment' && (
              <div className="om-field">
                <label>CNIC</label>
                <input type="text" value={form.cnic}
                  onChange={e => {
                    const d = e.target.value.replace(/\D/g, '').slice(0, 13);
                    let f = d;
                    if (d.length > 5)  f = d.slice(0, 5) + '-' + d.slice(5);
                    if (d.length > 12) f = d.slice(0, 5) + '-' + d.slice(5, 12) + '-' + d.slice(12);
                    set('cnic', f);
                  }}
                  placeholder="XXXXX-XXXXXXX-X" required maxLength={15} />
              </div>
            )}

            <div className="om-row">
              <div className="om-field">
                <label>Province</label>
                <select value={form.province}
                  onChange={e => { set('province', e.target.value); set('city', ''); }}
                  required>
                  <option value="">Select Province</option>
                  {Object.keys(PAKISTAN_LOCATIONS).map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div className="om-field">
                <label>City</label>
                <select value={form.city} onChange={e => set('city', e.target.value)}
                  required disabled={!form.province}>
                  <option value="">Select City</option>
                  {(PAKISTAN_LOCATIONS[form.province] || []).map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="om-field">
              <label>Complete Address</label>
              <textarea value={form.address} onChange={e => set('address', e.target.value)}
                placeholder="House/flat number, street, area" rows={2} required />
            </div>

            {type === 'cash' && (
              <div className="om-field">
                <label>Quantity</label>
                <input type="number" min="1" max="99" value={form.quantity}
                  onChange={e => set('quantity', e.target.value)} required />
              </div>
            )}

            {type === 'installment' && (
              <>
                <div className="om-row">
                  <div className="om-field">
                    <label>Job Title</label>
                    <input type="text" value={form.jobTitle} onChange={e => set('jobTitle', e.target.value)}
                      placeholder="e.g. Teacher, Engineer" required />
                  </div>
                  <div className="om-field">
                    <label>Monthly Income (Rs.)</label>
                    <input type="number" value={form.monthlyIncome}
                      onChange={e => set('monthlyIncome', e.target.value)}
                      placeholder="e.g. 50000" required />
                  </div>
                </div>

                {/* Plan was selected at the overview stage; show a compact
                    summary of the chosen plan so the customer can confirm
                    before submitting. To change it, use ← Back. */}
                {plans.length > 0 && (() => {
                  const plan = plans[Number(form.planIndex) || 0];
                  if (!plan) return null;
                  const r100 = (n) => Math.ceil((Number(n) || 0) / 100) * 100;
                  const months  = Number(plan.months) || 0;
                  const monthly = r100(plan.amount);
                  const advance = plan.advance?.enabled ? r100(plan.advance.amount) : 0;
                  const monthlyMonths = Math.max(0, months - 1);
                  const total = advance + monthly * monthlyMonths;
                  return (
                    <div className="om-plan-summary">
                      <div className="om-plan-summary-head">
                        <span className="om-plan-title">{months} Months Plan</span>
                        <button type="button" className="om-plan-summary-change"
                          onClick={() => { setStage('overview'); setSubmitError(''); }}>
                          Change
                        </button>
                      </div>
                      <div className="om-plan-summary-rows">
                        <span>Advance · Rs. {advance.toLocaleString()}</span>
                        <span>Monthly · Rs. {monthly.toLocaleString()} × {monthlyMonths}</span>
                        <span className="om-plan-summary-total">Total · Rs. {total.toLocaleString()}</span>
                      </div>
                    </div>
                  );
                })()}
              </>
            )}

            {submitError && (
              <p className="om-error">{submitError}</p>
            )}

            <button type="submit" className="om-submit" disabled={loading}>
              {loading ? 'Submitting…' : (type === 'cash' ? 'Place Order' : 'Submit Application')}
            </button>
          </form>
        ) : null}
      </div>
    </div>
  );
}

// ── Product Card ──────────────────────────────────────────────────────────────

function ProductCard({ product, type, saleBadge, priority = false }) {
  const images = productPrimaryImages(product);

  const [activeImg, setActiveImg] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const variants     = getVariants(product);
  const hasVariants  = variants.length > 0;
  const firstVariant = hasVariants ? variants[0] : null;
  const variantStartPrice = hasVariants ? variantCashPrice(firstVariant) : 0;
  const variantStartOrig  = hasVariants && firstVariant?.originalPrice
                         && Number(firstVariant.originalPrice) > variantStartPrice
                         ? Number(firstVariant.originalPrice) : 0;
  const hasDiscount  = hasVariants
    ? variantStartOrig > 0
    : product.discount?.discountedPrice && product.discount?.originalPrice;
  const isOutOfStock = product.inStock === false;

  const openModal = () => { trackView(product.id); setModalOpen(true); };

  return (
    <>
      <div className={`product-card${isOutOfStock ? ' out-of-stock' : ''}`}>
        {images.length > 0 && (
          <div className="product-image-wrap">
            <a href={createProductUrl(product)} style={{ display: 'block', width: '100%', height: '100%' }} aria-label={product.name}>
              <img src={images[activeImg]} alt={product.name} className="product-image" {...(priority ? { loading: 'eager', fetchPriority: 'high' } : { loading: 'lazy', decoding: 'async' })} />
            </a>
            {isOutOfStock && <span className="oos-badge">Out of Stock</span>}
            {!isOutOfStock && saleBadge && <span className="sale-badge">{saleBadge}</span>}
            {hasDiscount && (
              <span className="discount-badge">
                -{hasVariants
                  ? Math.round((1 - variantStartPrice / variantStartOrig) * 100)
                  : (product.discount.percentage ||
                      Math.round((1 - product.discount.discountedPrice / product.discount.originalPrice) * 100))}%
              </span>
            )}
            {images.length > 1 && (
              <div className="product-thumbs">
                {images.map((url, i) => (
                  <button key={i} className={`product-thumb${activeImg === i ? ' active' : ''}`}
                    onClick={() => setActiveImg(i)}>
                    <img src={url} alt="" loading="lazy" decoding="async" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="product-info">
          <a href={createProductUrl(product)} style={{ display: 'block', color: 'inherit', textDecoration: 'none' }}>
            {product.brand && <p className="product-brand">{product.brand}</p>}
            <h3>{product.name}</h3>
          </a>
          {type === 'installment' && (() => {
            const adv = lowestAdvance(product);
            if (adv === null) return null;
            return (
              <p className="product-advance-line">
                Rs. {adv.toLocaleString()} <span className="product-advance-word">Advance</span>
              </p>
            );
          })()}
          {product.description && (
            <div
              className="product-description rich-prose"
              dangerouslySetInnerHTML={{ __html: product.description }}
            />
          )}

          {type === 'cash' && (
            <div className="product-price-wrap">
              {hasVariants ? (
                <>
                  {variantStartOrig > 0 && (
                    <span className="product-price-original">Rs. {variantStartOrig.toLocaleString()}</span>
                  )}
                  <span className="product-price">From Rs. {variantStartPrice.toLocaleString()}</span>
                </>
              ) : hasDiscount ? (
                <>
                  <span className="product-price-original">
                    Rs. {Number(product.discount.originalPrice).toLocaleString()}
                  </span>
                  <span className="product-price">
                    Rs. {Number(product.discount.discountedPrice).toLocaleString()}
                  </span>
                </>
              ) : (
                <span className="product-price">
                  Rs. {Number(product.price).toLocaleString()}
                </span>
              )}
            </div>
          )}

          {isOutOfStock ? (
            <button className="view-btn" disabled>Out of Stock</button>
          ) : (
            <button className="order-btn" onClick={openModal}>
              {type === 'cash' ? 'Order Now' : 'Apply for Installment'}
            </button>
          )}
        </div>
      </div>

      {modalOpen && (
        <OrderModal product={product} type={type} onClose={() => setModalOpen(false)} />
      )}
    </>
  );
}

// ── Products Page ─────────────────────────────────────────────────────────────

function Products({ type, initialCategory, searchQuery = '', saleSettings }) {
  const [products, setProducts]     = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(initialCategory || 'all');

  useEffect(() => {
    let loadedProducts = false;
    let loadedCategories = false;
    const checkDone = () => { if (loadedProducts && loadedCategories) setLoading(false); };

    let unsubProducts;
    let unsubCategories;
    try {
      unsubProducts = db.subscribe('products', (data) => {
        if (data) {
          const list = Object.keys(data).map(key => ({ id: key, ...data[key] }));
          setProducts(list.filter(p => p.type === type || p.type === 'both'));
        } else {
          setProducts([]);
        }
        loadedProducts = true;
        checkDone();
      });
    } catch (e) {
      setError('Failed to load products.');
      setLoading(false);
    }
    try {
      unsubCategories = db.subscribe('categories', (data) => {
        if (data) {
          const list = Array.isArray(data) ? data : Object.values(data);
          setCategories(list.filter(Boolean));
        } else {
          setCategories([]);
        }
        loadedCategories = true;
        checkDone();
      });
    } catch (e) {
      setError(prev => prev || 'Failed to load categories.');
      setLoading(false);
    }

    return () => {
      if (typeof unsubProducts === 'function') unsubProducts();
      if (typeof unsubCategories === 'function') unsubCategories();
    };
  }, [type]);

  const categoryOptions = [
    { value: 'all', label: 'All Products' },
    ...categories.map(c => ({ value: c.name, label: c.name })),
  ];

  const categoryFiltered = selectedCategory === 'all'
    ? products
    : products.filter(p => p.category === selectedCategory);

  const q = searchQuery.trim().toLowerCase();
  const filteredProducts = (q
    ? categoryFiltered.filter(p =>
        p.name?.toLowerCase().includes(q) ||
        p.category?.toLowerCase().includes(q) ||
        p.brand?.toLowerCase().includes(q)
      )
    : [...categoryFiltered]
  ).sort((a, b) => {
    const brandCmp = (a.brand || '').localeCompare(b.brand || '');
    if (brandCmp !== 0) return brandCmp;
    return (a.name || '').localeCompare(b.name || '');
  });

  if (loading) return <div className="products-loading">Loading products...</div>;
  if (error)   return <div className="products-loading" style={{ color: 'red' }}>Failed to load products: {error}</div>;

  return (
    <div className="products-container">
      <div className="category-filter">
        {categoryOptions.map(cat => (
          <button
            key={cat.value}
            className={selectedCategory === cat.value ? 'active' : ''}
            onClick={() => setSelectedCategory(cat.value)}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {filteredProducts.length === 0 ? (
        <div className="no-products"><p>No products available yet.</p></div>
      ) : (
        <div className="products-grid">
          {filteredProducts.map((product, i) => (
            <ProductCard key={product.id} product={product} type={type} saleBadge={saleSettings?.enabled && saleSettings?.endDate && new Date(saleSettings.endDate) >= new Date() ? saleSettings.badgeText : null} priority={i === 0} />
          ))}
        </div>
      )}
    </div>
  );
}

export default Products;
